'use client'

import { useProductDetailSafe } from '@/app/[locale]/product/[slug]/context/product-detail-context'
import { useProductDetailStore } from '@/app/[locale]/product/[slug]/hooks/use-product-detail-store'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { useCampaignPrice } from '@/hooks/common/use-campaign-price'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn, formatPrice, stringToSlug } from '@/lib/utils'
import { Option } from '@/types/product'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import './liquid-glass.css'

interface InfoBarMobileQrProps {
  isStopSale?: boolean
  isGiveaway?: boolean
}

/**
 * Card giá/màu/size custom cho layout QR (?utm_source=qr&template=enhance_visual).
 * 2 trạng thái vị trí dựa theo isPageScrolledPastGallery: còn trong gallery
 * → absolute bám đáy ảnh (ảnh không còn full height nên card phải đi theo
 * đáy ảnh thật); đã cuộn qua khỏi gallery → fixed dính đáy màn hình như
 * sticky bar bình thường.
 */
export const InfoBarMobileQr = ({
  isStopSale,
  isGiveaway,
}: InfoBarMobileQrProps) => {
  const {
    product,
    colorOptionActive,
    sortedColorOptions,
    colorSelected,
    sizeOptions,
    sizeSelected,
    setColorSelected,
    setColorOptionActive,
    setSizeSelected,
    isPendingAddToCart,
    scrollToFirstImageRef,
    activeGalleryIndex,
    scrollToGalleryIndexRef,
  } = useProductDetailSafe()
  const { isDisableSize } = useProductDetailStore()
  const t = useTranslations('product_detail.title')
  const searchParams = useSearchParams()

  // colorOptionActive chỉ được set qua effect (initializeColorFromURL trong
  // useProductDetailStore), nên ở lần render đầu nó vẫn null → gallery/preview
  // tạm fallback về product.images (ảnh mặc định, có thể không đúng màu từ
  // URL). Tự resolve màu ngay ở đây (đồng bộ, không chờ effect) để tránh
  // flash sai ảnh lúc load lần đầu.
  const resolvedColorOption = useMemo(() => {
    if (colorOptionActive) return colorOptionActive
    const colorFromURL = searchParams.get('color')
    const matched = sortedColorOptions.find(
      (option) => stringToSlug(option.title) === colorFromURL
    )
    return matched || sortedColorOptions[0] || null
  }, [colorOptionActive, searchParams, sortedColorOptions])

  const productImage = (
    resolvedColorOption?.image?.slice(0, -1) ||
    product?.images?.slice(0, -1) ||
    []
  ).slice(-3)

  // Đổi màu text theo TÊN màu variant đang chọn (không phải detect pixel ảnh):
  // màu trắng → chữ đen, các màu còn lại (kể cả đen) → chữ trắng.
  const isWhiteColorVariant = useMemo(() => {
    if (!resolvedColorOption?.title) return false
    const words = stringToSlug(resolvedColorOption.title).split('-')
    return words.includes('trang') || words.includes('white')
  }, [resolvedColorOption])

  const glassTextColor = isWhiteColorVariant ? 'text-neutral-900' : 'text-white'

  // Ẩn hẳn màu hết hàng (tất cả size của màu đó đều isDisable) thay vì hiện
  // mờ — layout QR ưu tiên gọn, chỉ show lựa chọn còn mua được.
  const availableColorOptions = sortedColorOptions.filter((variant) => {
    const colorKey = Object.keys(product.mapped_variants || {}).find(
      (key) => stringToSlug(key) === stringToSlug(variant.title)
    )
    if (!colorKey) return true
    return product.mapped_variants[colorKey].some((v) => !v.isDisable)
  })

  // Ẩn hẳn size hết hàng của màu đang chọn, cùng lý do như trên.
  const availableSizeOptions = sizeOptions.filter(
    (size) => !isDisableSize(size)
  )

  // Pill nền trượt mượt theo size đang chọn thay vì đổi màu tức thì.
  const sizeRowRef = useRef<HTMLUListElement>(null)
  const sizeRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const [sizePillStyle, setSizePillStyle] = useState({ left: 0, width: 0 })

  useEffect(() => {
    const updatePillPosition = () => {
      const activeEl = sizeSelected ? sizeRefs.current[sizeSelected] : null
      if (!activeEl) return
      setSizePillStyle({
        left: activeEl.offsetLeft,
        width: activeEl.offsetWidth,
      })
    }

    updatePillPosition()

    // Hàng size đổi width bất cứ khi nào cột thumbnail preview bên cạnh co
    // giãn (lúc cuộn trang) hoặc khi resize — ResizeObserver theo dõi liên
    // tục để pill luôn bám đúng vị trí, thay vì chỉ đo một lần lúc chọn size.
    const row = sizeRowRef.current
    if (!row) return
    const resizeObserver = new ResizeObserver(updatePillPosition)
    resizeObserver.observe(row)
    return () => resizeObserver.disconnect()
  }, [sizeSelected, sizeOptions, colorSelected])

  // Ẩn dải thumbnail preview khi trang đã cuộn qua khỏi gallery (h-dvh đầu
  // tiên) — không còn tác dụng điều hướng ảnh vì gallery đã ngoài viewport.
  const [isPageScrolledPastGallery, setIsPageScrolledPastGallery] =
    useState(false)

  useEffect(() => {
    const handleWindowScroll = () => {
      setIsPageScrolledPastGallery(window.scrollY > 0)
    }
    handleWindowScroll()
    window.addEventListener('scroll', handleWindowScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleWindowScroll)
  }, [])

  // Đã cuộn qua khỏi gallery: bg/text cố định theo Figma (không còn check tên
  // màu variant nữa — glassTextColor chỉ áp dụng khi còn ở trong gallery).
  const textColor = isPageScrolledPastGallery
    ? 'text-neutral-800'
    : glassTextColor

  // Ẩn hẳn card khi CTA "Thêm vào giỏ hàng" thật (#product-detail-add-cart,
  // render trong ProductInfoView bên dưới) đã lọt vào viewport — tránh hiện
  // 2 nút mua hàng cùng lúc trên màn hình. Logic giống hệt InfoBarMobile gốc:
  // re-query getElementById trên mỗi lần scroll (thay vì IntersectionObserver
  // gắn 1 lần lúc mount) — vì ProductInfoView là dynamic import riêng, có thể
  // chưa mount xong tại thời điểm effect này chạy lần đầu.
  const [isRealAddCartVisible, setIsRealAddCartVisible] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const addCartEl = document.getElementById('product-detail-add-cart')
      if (!addCartEl) return
      const rect = addCartEl.getBoundingClientRect()
      const isInViewport = rect.top <= window.innerHeight && rect.bottom >= 0
      setIsRealAddCartVisible(isInViewport)
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const { finalPrice, originalPrice, discountPercent, shouldShowDiscount } =
    useCampaignPrice({
      regularPrice: product.regular_price,
      comparePrice: product.compare_price,
      product,
    })

  const displayPrice = shouldShowDiscount ? finalPrice : product.regular_price
  const displayComparePrice =
    shouldShowDiscount && originalPrice !== finalPrice
      ? originalPrice
      : product.compare_price
  const displayDiscountPercent = shouldShowDiscount
    ? discountPercent
    : product.sale_percent

  // Nếu short_desc có thẻ <br> đầu tiên, cắt chuỗi tại đó và thêm "..." (bỏ
  // phần sau <br>, thường là ý phụ). Dù có cắt hay không, vẫn luôn áp
  // line-clamp-3 để chặn tràn khi phần trước <br> tự nó đã dài.
  const shortDesc = product.short_desc
  const shortDescBrIndex = useMemo(
    () => shortDesc?.search(/<\/?br\s*\/?>/i) ?? -1,
    [shortDesc]
  )
  const shortDescHtml = !shortDesc
    ? null
    : shortDescBrIndex === -1
      ? shortDesc
      : shortDesc.slice(0, shortDescBrIndex)

  const handleColorClick = (variant: Option) => {
    setColorSelected(stringToSlug(variant.title))
    setColorOptionActive(variant)
    scrollToFirstImageRef.current?.()
  }

  const triggerAddToCartButton = () => {
    if (isPendingAddToCart) return
    document.getElementById('product-detail-add-cart')?.click()
  }

  // Ẩn hẳn nút đặt hàng khi sản phẩm coming soon hoặc hết hàng (không còn
  // màu/size nào) — cùng điều kiện với InfoBarAction bản desktop/mobile mặc
  // định (info-action.tsx), layout QR ưu tiên gọn nên ẩn hẳn thay vì disable.
  const isComingSoon = Boolean(product?.coming_soon)
  const isOutOfStock = !sortedColorOptions.length && !sizeOptions.length
  const canOrder = !isComingSoon && !isOutOfStock

  // Bấm vào vùng nền của card (không phải các nút chọn màu/size/mua hàng bên
  // trong, các nút đó đã stopPropagation) sẽ scroll xuống section "Why we
  // make it?" — giống hành vi bấm title ở ProductGalleryQr.
  const handleScrollToWhyWeMakeIt = () => {
    document
      .getElementById('why-we-make-it')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (isGiveaway) return null

  return (
    <div
      onClick={handleScrollToWhyWeMakeIt}
      className={cn(
        'inset-x-0 bottom-0 z-40 flex items-end px-5 py-4 transition-all duration-300 ease-out',
        // Còn trong gallery: absolute bám đáy ảnh (ảnh giờ không còn full
        // height nên card phải đi theo đáy ảnh thật). Đã cuộn qua khỏi
        // gallery: chuyển fixed để card dính đáy màn hình như một sticky bar
        // bình thường.
        isPageScrolledPastGallery ? 'fixed' : 'absolute',
        // Ẩn bằng CSS (không unmount) khi CTA thật đã hiện — unmount qua
        // return null làm mất sizeRefs/ResizeObserver của pill size, khiến
        // pill định vị sai khi component remount lúc cuộn qua lại vùng CTA.
        isRealAddCartVisible && 'pointer-events-none translate-y-full opacity-0'
      )}
      inert={isRealAddCartVisible}
    >
      {productImage.length > 1 && (
        <div
          className={cn(
            'flex flex-col gap-2 overflow-hidden transition-all duration-300 ease-out',
            isPageScrolledPastGallery
              ? 'mr-0 w-0 opacity-0'
              : 'mr-2 w-10 opacity-100'
          )}
          inert={isPageScrolledPastGallery}
        >
          {productImage.map((item, index) => (
            <button
              key={index}
              type='button'
              className='relative h-10 min-h-10 w-10 min-w-10 overflow-hidden rounded border border-white/40'
              onClick={(e) => {
                e.stopPropagation()
                scrollToGalleryIndexRef.current?.(index)
              }}
            >
              <Image
                src={ImageHelper.getImageUrl(item.src, '270_306')}
                alt={product.display_name + ' - ' + resolvedColorOption?.title}
                width={30}
                height={40}
                className='h-auto w-full object-cover'
                unoptimized
              />
              {activeGalleryIndex !== index && (
                <span className='absolute inset-0 bg-black/20' />
              )}
            </button>
          ))}
        </div>
      )}

      <div className='flex flex-1 flex-col gap-2'>
        <div className='liquid-glass rounded-2xl p-4'>
          {shortDescHtml && !isPageScrolledPastGallery && (
            <p
              className={cn(textColor, 'mb-3 line-clamp-3 text-sm leading-5')}
              dangerouslySetInnerHTML={{ __html: shortDescHtml }}
            />
          )}
          <div className='flex items-end justify-between gap-2'>
            <div className='flex flex-col gap-1'>
              {displayComparePrice && displayComparePrice !== displayPrice ? (
                <del className={cn(textColor, 'text-xs opacity-60')}>
                  {formatPrice(displayComparePrice)}
                </del>
              ) : null}
              <div className='flex items-center gap-1'>
                <p className={cn(textColor, 'text-sm font-bold')}>
                  {formatPrice(displayPrice)}
                </p>
                {displayDiscountPercent && displayDiscountPercent > 0 ? (
                  <TagThumbnail
                    title={`-${displayDiscountPercent}%`}
                    size='small'
                    displayDiscountPercent={displayDiscountPercent}
                  />
                ) : null}
              </div>
            </div>

            {availableColorOptions.length > 0 && (
              <ul className='flex flex-wrap items-center justify-end gap-1.5'>
                {availableColorOptions.map((variant) => {
                  const isActive = colorSelected === stringToSlug(variant.title)
                  const swatchImage =
                    variant?.image?.[variant.image.length - 1]?.src
                  return (
                    <li key={variant.title}>
                      <button
                        type='button'
                        onClick={(e) => {
                          e.stopPropagation()
                          handleColorClick(variant)
                        }}
                        aria-label={variant.title}
                        aria-pressed={isActive}
                        className={cn(
                          'block h-6 w-6 overflow-hidden rounded-full border-2',
                          isActive ? 'border-primary' : 'border-white/40'
                        )}
                      >
                        <Image
                          src={ImageHelper.getImageUrl(swatchImage, '50_67')}
                          alt={variant.title}
                          width={20}
                          height={20}
                          className='h-full w-full object-cover'
                          unoptimized
                        />
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        <div className='flex items-center justify-end gap-2'>
          {availableSizeOptions.length > 0 && (
            <ul
              ref={sizeRowRef}
              className='liquid-glass relative flex flex-1 items-center gap-1 rounded-full p-1'
            >
              {sizePillStyle.width > 0 && (
                <div
                  className={cn(
                    'pointer-events-none absolute inset-y-1 z-0 rounded-full transition-all duration-300 ease-out',
                    isPageScrolledPastGallery ? 'bg-neutral-800' : 'bg-white'
                  )}
                  style={{
                    left: sizePillStyle.left,
                    width: sizePillStyle.width,
                  }}
                />
              )}
              {availableSizeOptions.map((size) => {
                const isActive = sizeSelected === size
                return (
                  <li key={size} className='flex-1'>
                    <button
                      type='button'
                      ref={(el) => {
                        sizeRefs.current[size] = el
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setSizeSelected(size)
                      }}
                      className={cn(
                        'relative z-10 flex h-8 w-full items-center justify-center rounded-full text-sm transition-colors duration-300',
                        isActive
                          ? isPageScrolledPastGallery
                            ? 'text-white'
                            : 'text-neutral-900'
                          : textColor
                      )}
                    >
                      {size}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          {canOrder && (
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation()
                triggerAddToCartButton()
              }}
              disabled={isStopSale || isPendingAddToCart}
              className='shrink-0 rounded-full bg-primary px-4 py-2 text-[10px] font-bold uppercase text-white disabled:opacity-50'
              ga-tracking-value='product-detail__add_to_cart_qr'
              ga-tracking-label='product-detail__add_to_cart_qr'
            >
              {isStopSale
                ? t('stopped_selling')
                : isPendingAddToCart
                  ? t('adding')
                  : t('order_now')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
